import {
  configureStore,
  type EnhancedStore,
  type StoreEnhancer,
  type ThunkDispatch,
  type ThunkMiddleware,
  type Tuple,
  type UnknownAction,
} from '@reduxjs/toolkit';
import { hydrateRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import createSagaMiddleware, { type SagaMiddleware } from 'redux-saga';

// import pckg from '../../package.json';
// import './style.scss';
import i18n from '../i18next-react';
import { getLegalNamespacesForPath } from '../legal/legal-versions';
import ErrorBoundary from './components/error-boundary';
import ScrollToTop from './components/scroll-to-top';
import Layout from './containers/layout';
import { initErrorReporter } from './error-reporter';
import rootReducer, { type RootState } from './reducers/customer';
import { getSocket, mySaga } from './saga';
import Socket from './socket';
import { captureAttribution } from './utils/attribution';
import { initWebVitals } from './web-vitals';

const sagaMiddleware: SagaMiddleware<object> = createSagaMiddleware();

const preloadedState: RootState | undefined = window.__PRELOADED_STATE__;
delete window.__PRELOADED_STATE__;

const store: EnhancedStore<
  RootState,
  UnknownAction,
  Tuple<
    [
      StoreEnhancer<{
        dispatch: ThunkDispatch<RootState, undefined, UnknownAction>;
      }>,
      StoreEnhancer,
    ]
  >
> = configureStore({
  reducer: rootReducer,
  preloadedState,
  middleware: (
    getDefaultMiddleware,
  ): Tuple<
    [ThunkMiddleware<RootState, UnknownAction>, SagaMiddleware<object>]
  > => getDefaultMiddleware().concat(sagaMiddleware),
});

sagaMiddleware.run(mySaga);

const { code } = store.getState().Language.languageType;

const customerElemenet: HTMLElement | null =
  document.getElementById('customer');

if (!customerElemenet) {
  throw new Error('Customer element not found!');
}

i18n
  .changeLanguage(code.toLowerCase())
  .then((): Promise<void> =>
    i18n.loadNamespaces(getLegalNamespacesForPath(window.location.pathname)),
  )
  .then((): void => {
    hydrateRoot(
      customerElemenet,
      <BrowserRouter>
        <Provider store={store}>
          <I18nextProvider i18n={i18n}>
            <ErrorBoundary>
              <ScrollToTop />
              <Layout />
            </ErrorBoundary>
          </I18nextProvider>
        </Provider>
      </BrowserRouter>,
      {
        onRecoverableError(
          error: unknown,
          errorInfo: { componentStack?: string | null },
        ) {
          // eslint-disable-next-line no-console
          console.error('[hydrateRoot] recoverable error:', error);
          if (errorInfo.componentStack) {
            // eslint-disable-next-line no-console
            console.error(
              '[hydrateRoot] component stack:',
              errorInfo.componentStack,
            );
          }
        },
      },
    );

    const socket = new Socket(getSocket(), store);
    socket.requests();
    socket.subscribe();

    captureAttribution();
    initErrorReporter();
  });

// web-vitals handles its own timing (LCP after load, CLS/INP on
// visibilitychange); pushes queue in window.dataLayer until GTM drains it.
initWebVitals();
